/**
 * RecipeChooser — default-first template picker (Phase 3, R5).
 *
 * Presentation-only seam: props in, {recipeId} out (R4 contract).
 * Default-first per R5: Recommended pre-selected + Recently-used row +
 * inline-expanding full list. NO second modal.
 *
 * A5 discipline: current design system only — no §4.2 spec lines, no §4.3
 * anatomy pane, no card redesign. That ships in the separate anatomy PR.
 *
 * gate_conflict (A1): conflicting recipes are EXCLUDED from Recommended,
 * shown in the full list with a warning badge. Yours recipes are always
 * visible (warned, not hidden).
 */

import React, { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PICKER_STRINGS } from './pickerStrings';

// The four templates Quick Create pins to, by their stable seed slugs (#1308):
// Default SEO (600w) · Standard Informational (1500w) · How-To Guide · Listicle.
// Order here is the order shown. Kept in sync with 20260121_seed_auto_templates.sql.
const QUICK_CREATE_SLUGS = [
  'auto-short-informational',
  'auto-standard-informational',
  'auto-how-to-guide',
  'auto-listicle',
] as const;

export interface RecipeOption {
  id: string;
  name: string;
  /** Stable seed slug (e.g. 'auto-listicle'). Used to pin the Quick Create set. */
  slug?: string;
  description?: string;
  is_system: boolean;
  is_customized: boolean;
  gate_conflict: boolean;
  shape_floor?: {
    rules?: { min_h2?: number; min_words?: number };
  };
}

export interface RecipeChooserProps {
  /** All available templates (from the list response). */
  templates: RecipeOption[];
  /** IDs from the per-tenant recently-used query (recency-ordered). */
  recentlyUsedIds: string[];
  /** IDs from the recommended endpoint (list_recommended). When present,
   *  Recommended derives from these IDs; falls back to client-side heuristic
   *  when absent/empty (R5 source + endpoint resilience). */
  recommendedIds?: string[];
  /** Pre-selected recipe ID (from Recommended or caller state). */
  selectedRecipeId?: string | null;
  /** Called when the user selects a recipe — bare string recipeId (R4 contract). */
  onSelect: (recipeId: string) => void;
  /** Called when the user confirms — bare string recipeId (R4 contract). */
  onConfirm: (recipeId: string) => void;
  /** Called when the user cancels. */
  onCancel?: () => void;
  /** Select mode: compact layout for modal embedding. Browse All still available. */
  selectMode?: boolean;
  /** Quick Create v2 (#row3): drop the Recommended + Recently-Used sections and
   *  show a flat list of just 4 templates (Browse All still available). */
  simplified?: boolean;
  className?: string;
}

export const RecipeChooser: React.FC<RecipeChooserProps> = ({
  templates,
  recentlyUsedIds,
  recommendedIds,
  selectedRecipeId,
  onSelect,
  onConfirm,
  onCancel,
  selectMode = false,
  simplified = false,
  className,
}) => {
  const [showAll, setShowAll] = useState(false);
  const [localSelected, setLocalSelected] = useState<string | null>(
    selectedRecipeId ?? null,
  );

  // Recommended: from recommendedIds (R5 source: list_recommended endpoint) when
  // available, with A1 gate_conflict exclusion applied client-side. Falls back to
  // client-side heuristic (!gate_conflict && is_system) when recommendedIds is
  // absent/empty (endpoint failure resilience).
  const recommended = useMemo(() => {
    if (recommendedIds && recommendedIds.length > 0) {
      const byId = new Map(templates.map((t) => [t.id, t]));
      return recommendedIds
        .map((id) => byId.get(id))
        .filter((t): t is RecipeOption => t !== undefined && !t.gate_conflict);
    }
    return templates.filter((t) => !t.gate_conflict && t.is_system);
  }, [templates, recommendedIds]);

  // Pre-select the first recommended if nothing is selected
  const effectiveSelected = localSelected ?? recommended[0]?.id ?? null;

  // Recently-used templates (cross-reference IDs against loaded templates)
  const recentTemplates = useMemo(() => {
    const byId = new Map(templates.map((t) => [t.id, t]));
    return recentlyUsedIds
      .map((id) => byId.get(id))
      .filter((t): t is RecipeOption => t !== undefined);
  }, [templates, recentlyUsedIds]);

  // Full list (all templates, including conflicting ones with warning)
  const allTemplates = templates;

  // Quick Create v2: the flat list is PINNED to the four agreed auto templates
  // (matched by their stable seed slugs, shown in QUICK_CREATE_SLUGS order) so
  // the client always sees exactly Default-SEO-600 / Info-1500 / How-to / Listicle
  // regardless of how many system templates the backend returns (#1308). If the
  // slugs aren't present (older data / customized-only tenants), fall back to
  // topping up from the first non-conflicting (system-first) recipes, capped at 4.
  const simpleTop = useMemo(() => {
    const bySlug = new Map(
      allTemplates.filter((t) => t.slug).map((t) => [t.slug as string, t]),
    );
    const pinned = QUICK_CREATE_SLUGS
      .map((slug) => bySlug.get(slug))
      .filter((t): t is RecipeOption => t !== undefined);
    if (pinned.length === 4) return pinned;

    const nonConflicting = allTemplates.filter((t) => !t.gate_conflict);
    const system = nonConflicting.filter((t) => t.is_system);
    const pool = system.length >= 4 ? system : nonConflicting;
    const seen = new Set(pinned.map((t) => t.id));
    const filled = [...pinned];
    for (const t of pool) {
      if (filled.length >= 4) break;
      if (!seen.has(t.id)) {
        filled.push(t);
        seen.add(t.id);
      }
    }
    return filled.slice(0, 4);
  }, [allTemplates]);

  const handleSelect = (id: string) => {
    setLocalSelected(id);
    onSelect(id);
  };

  const handleConfirm = () => {
    if (effectiveSelected) {
      onConfirm(effectiveSelected);
    }
  };

  return (
    <div className={cn("flex flex-col gap-3 p-4", className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-base">{PICKER_STRINGS.title}</h3>
          <p className="text-sm text-muted-foreground">{PICKER_STRINGS.subtitle}</p>
        </div>
        {onCancel && (
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </div>

      {/* Quick Create v2: flat list of 4 templates (no Recommended/Recently). */}
      {simplified && (
        <div className="space-y-1">
          {simpleTop.map((t) => (
            <RecipeRow
              key={t.id}
              template={t}
              selected={effectiveSelected === t.id}
              onSelect={handleSelect}
            />
          ))}
        </div>
      )}

      {/* Recommended */}
      {!simplified && recommended.length > 0 && (
        <div className="space-y-1.5">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            {PICKER_STRINGS.recommended}
          </span>
          {recommended.slice(0, 1).map((t) => (
            <RecipeRow
              key={t.id}
              template={t}
              selected={effectiveSelected === t.id}
              onSelect={handleSelect}
            />
          ))}
        </div>
      )}

      {/* Recently Used */}
      {!simplified && recentTemplates.length > 0 && (
        <div className="space-y-1.5">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            {PICKER_STRINGS.recentlyUsed}
          </span>
          <div className="flex flex-wrap gap-1.5">
            {recentTemplates.map((t) => (
              <button
                key={t.id}
                onClick={() => handleSelect(t.id)}
                className={cn(
                  "px-2.5 py-1 rounded-md text-xs border transition-colors",
                  effectiveSelected === t.id
                    ? "border-primary bg-primary/10"
                    : "border-border hover:border-primary/50",
                )}
              >
                {t.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Browse All (inline expand — NO second modal per R5) */}
      {allTemplates.length > (simplified ? simpleTop.length : recommended.length) && (
        <div className="space-y-1.5">
          <button
            onClick={() => setShowAll(!showAll)}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {showAll ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
            {PICKER_STRINGS.browseAll} ({allTemplates.length})
          </button>

          {showAll && (
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {allTemplates.map((t) => (
                <RecipeRow
                  key={t.id}
                  template={t}
                  selected={effectiveSelected === t.id}
                  onSelect={handleSelect}
                  showConflictWarning
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Confirm/Cancel */}
      <div className="flex gap-2 pt-2">
        <Button
          onClick={handleConfirm}
          disabled={!effectiveSelected}
          className="flex-1 bg-coral hover:bg-coral-hover"
        >
          {PICKER_STRINGS.useThisTemplate}
        </Button>
      </div>
    </div>
  );
};

// --- Internal: single recipe row ---

const RecipeRow: React.FC<{
  template: RecipeOption;
  selected: boolean;
  onSelect: (id: string) => void;
  showConflictWarning?: boolean;
}> = ({ template, selected, onSelect, showConflictWarning }) => {
  return (
    <div
      onClick={() => onSelect(template.id)}
      className={cn(
        "flex items-center justify-between p-2.5 rounded-md border cursor-pointer transition-colors",
        selected
          ? "border-primary border-2 bg-primary/5"
          : "border-border hover:border-primary/50",
      )}
    >
      <div className="min-w-0 flex-1">
        <span className="text-sm font-medium truncate block">{template.name}</span>
        {template.description && (
          <span className="text-xs text-muted-foreground truncate block">
            {template.description}
          </span>
        )}
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {showConflictWarning && template.gate_conflict && (
          <Badge variant="outline" className="text-amber-dark bg-amber-light text-[10px] px-1.5">
            <AlertCircle className="h-3 w-3 mr-0.5" />
            {PICKER_STRINGS.floorConflict}
          </Badge>
        )}
        {!template.is_system && (
          <Badge variant="outline" className="bg-teal-light text-teal-dark text-[10px] px-1.5">
            {PICKER_STRINGS.yours}
          </Badge>
        )}
        {selected && (
          <span className="text-primary text-sm">✓</span>
        )}
      </div>
    </div>
  );
};
